/**
 * features/comments/ReviewWorkspace.tsx
 * ------------------------------------------------------------------
 * Al-Nur-style dual-pane review screen, shared by the Head of Year
 * and the Class Teacher comment workflows.
 *
 *   LEFT  (context, read-only): student summary + every subject with
 *         its mark/grade and the SUBJECT TEACHER's comment.
 *         When field === 'hodComment', the CLASS TEACHER's comment
 *         is also shown below the subjects (read-only quote block)
 *         so HOY has the full picture before writing.
 *   RIGHT (action): the reviewer's own comment box, suggestions, and
 *         click-to-insert template phrases.
 *   BOTTOM: student navigator — Previous · dropdown · Next, progress
 *         bar, "Student n of N", "x/N complete".
 *
 * Round 9 visual refresh: unified on SoftCard / StatChip /
 * AccentButton primitives. Reduced divider clutter. Subtler subject
 * cards with hover lift. Progress bar instead of capped dots. New
 * class-teacher block for HOY view.
 * ------------------------------------------------------------------
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
// Comments foundation swap — route setDoc through the undefined-safe wrapper
import { setDoc } from '@/lib/firebase';
import {
  Lock,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  MessageSquare,
  Check,
  Flag,
  CalendarX,
  ClipboardCheck,
  Save as SaveIcon,
  UserCheck,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { useDirtyFlag } from '@/contexts/DirtyContext';
import { assembleClassReport } from '@/lib/assembleReport';
import { sanitizeComment, stripUndefined } from '@/lib/sanitize';
import { auditFromContext } from '@/lib/audit';
import { toLegacyTotals } from '@/lib/attendanceCalc';
import { gradeColor } from '@/lib/grading';
import {
  getMarkCategory,
  BUILT_IN_FALLBACKS,
  suggestComment,
} from '@/lib/commentTemplates';
import { Spinner, Button } from '@/components/ui/primitives';
import { SaveConfirmation, useAutoSave, StickySaveBar } from '@/components/ui/SaveConfirmation';
import RaiseFlagModal from './RaiseFlagModal';
import { getPermissions } from '@/lib/permissions';
import {
  GradientHero,
  GlassPill,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/components/ui/glass';
import { TermKey, termLabel } from '@/lib/school.config';
import type { ReportStudent } from '@/lib/reportPdf';
import type {
  ReportCommentsDocument,
  StudentReportComments,
  SchoolClass,
} from '@/types';

export type ReviewField = 'hodComment' | 'classTeacherComment';

interface TemplateGroup {
  label: string;
  phrases: string[];
}

interface Props {
  year: string;
  term: TermKey;
  /** Which comment field this workspace edits. */
  field: ReviewField;
  title: string;
  subtitle: string;
  /** Classes the signed-in user may review (already scoped). */
  classes: SchoolClass[];
  /** Whether the term is editable for this user. */
  editable: boolean;
  termStateLabel: string;
  /** Per-student permission check. */
  canEditStudent: (studentId: string) => boolean;
  /** Click-to-insert template suggestions. */
  templates: TemplateGroup[];
}

const rgb = (c: [number, number, number]) => `rgb(${c[0]},${c[1]},${c[2]})`;

export default function ReviewWorkspace({
  year,
  term,
  field,
  title,
  subtitle,
  classes,
  editable,
  termStateLabel,
  canEditStudent,
  templates,
}: Props) {
  const { activeStudents: students, subjects, teachers, classes: allClasses } = useData();
  const { userData, userRole, userRoles } = useAuth();
  const canRaiseFlags = getPermissions(userRoles).canRaiseFlags;
  const [flagFor, setFlagFor] = useState<null | {
    subjectName: string;
    subjectId?: string;
    teacherName?: string;
    commentPreview?: string;
  }>(null);

  const [classId, setClassId] = useState('');
  const [rows, setRows] = useState<ReportStudent[]>([]);
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [daysAbsent, setDaysAbsent] = useState<Record<string, number>>({});
  const [existing, setExisting] = useState<Record<string, StudentReportComments>>({});
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Register unsaved comment edits so the version-gate reload can warn before
  // discarding them (a teacher lost comments to a blind reload — Aug 2026).
  useDirtyFlag('review-comments', dirty && !!classId && editable);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Deep-link from a "your comment was flagged" notification: if the marker is
  // for THIS field (class-teacher or HOY comment), open that class and jump to
  // the pupil. Subject-comment markers are left for the Mark Entry screen.
  const flagFixDone = useRef(false);
  const [pendingFlagStudent, setPendingFlagStudent] = useState<string | null>(null);
  useEffect(() => {
    if (flagFixDone.current || classes.length === 0) return;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem('lkc.commentFlagFix'); } catch { /* ignore */ }
    if (!raw) return;
    let d: { target?: string; classId?: string; studentId?: string };
    try { d = JSON.parse(raw); } catch { return; }
    if (d.target !== field) return; // not the marker for this screen
    flagFixDone.current = true;
    try { sessionStorage.removeItem('lkc.commentFlagFix'); } catch { /* ignore */ }
    if (d.classId) setClassId(d.classId);
    if (d.studentId) setPendingFlagStudent(d.studentId);
  }, [classes, field]);
  useEffect(() => {
    if (!pendingFlagStudent || studentIds.length === 0) return;
    const i = studentIds.indexOf(pendingFlagStudent);
    if (i >= 0) setIdx(i);
    setPendingFlagStudent(null);
  }, [pendingFlagStudent, studentIds]);
  // Round 12: full-screen save modal replaces the inline toast for the save flow.
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);

  const taRef = useRef<HTMLTextAreaElement>(null);

  const roster = useMemo(
    () =>
      students
        .filter((s) => s.currentClass === classId)
        .sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [students, classId],
  );

  const load = useCallback(async () => {
    if (!classId) return;
    setLoading(true);
    setMsg(null);
    try {
      // left-pane context (marks + subject teacher comments)
      const assembled = await assembleClassReport({
        year,
        term,
        classId,
        classes: allClasses,
        students,
        subjects,
        teachers,
      });
      setRows(assembled);

      const id = `${year}-${term}-${classId}`;
      const snap = await getDoc(doc(db, COLLECTIONS.reportComments, id));
      const entries =
        (snap.exists()
          ? (snap.data() as ReportCommentsDocument).entries
          : {}) ?? {};
      setExisting(entries);

      // Days absent — computed from daily attendance records
      const attSnap = await getDocs(
        query(
          collection(db, COLLECTIONS.dailyAttendance),
          where('classId', '==', classId),
        ),
      );
      const dailyDocs = attSnap.docs.map(
        (d) => d.data() as import('@/types').DailyAttendanceDoc,
      );
      const totals = toLegacyTotals(dailyDocs, roster.map((s) => s.id));

      const next: Record<string, string> = {};
      const abs: Record<string, number> = {};
      roster.forEach((s) => {
        next[s.id] = entries[s.id]?.[field] ?? '';
        abs[s.id] = totals[s.id]?.daysAbsent ?? 0;
      });
      setDrafts(next);
      setDaysAbsent(abs);
      setStudentIds(roster.map((s) => s.id));
      setIdx(0);
      setDirty(false);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, year, term, roster.length]);

  useEffect(() => {
    if (classId) load();
  }, [classId, year, term, load]);

  const currentId = studentIds[idx];
  const currentStudent = roster.find((s) => s.id === currentId);
  const currentRow = rows.find((r) => r.name === currentStudent?.fullName);
  const canEditThis = currentId ? canEditStudent(currentId) : false;

  const completeCount = useMemo(
    () =>
      studentIds.filter((id) => (drafts[id] ?? '').trim().length > 0).length,
    [studentIds, drafts],
  );
  const completePct =
    studentIds.length > 0
      ? Math.round((completeCount / studentIds.length) * 100)
      : 0;

  function setDraft(v: string) {
    if (!currentId) return;
    setDrafts((d) => ({ ...d, [currentId]: v }));
    setDirty(true);
    touchAutoSave();
  }

  function insertTemplate(phrase: string) {
    if (!currentId || !canEditThis) return;
    const cur = drafts[currentId] ?? '';
    const joined = cur.trim().length ? cur.trim() + ' ' + phrase : phrase;
    setDraft(joined);
    taRef.current?.focus();
  }

  // "AI Generate" — template-only mode (Al-Nur's documented
  // zero-cost fallback path). Computes the student's average ->
  // mark category -> a built-in/approved template, filled with the
  // first name. The live LLM path is wired when LKC commits;
  // contract is identical.
  function aiSuggest() {
    if (!currentId || !canEditThis || !currentStudent) return;
    const subs = currentRow?.subjects ?? [];
    const ms = subs
      .map((x) => (x.exam === 'ABS' ? null : x.exam ?? x.termMark))
      .filter((n): n is number => typeof n === 'number');
    const avg = ms.length
      ? Math.round(ms.reduce((a, b) => a + b, 0) / ms.length)
      : 60;
    const cat = getMarkCategory(avg);
    const pool = BUILT_IN_FALLBACKS[cat];
    const seed = (drafts[currentId] ?? '').length + ms.length;
    const phrase = suggestComment(currentStudent.fullName, cat, pool, seed, currentStudent.gender);
    setDraft(phrase);
    taRef.current?.focus();
    setMsg({
      ok: true,
      text: 'Suggested from approved templates. Review & edit before saving.',
    });
    setTimeout(() => setMsg(null), 3000);
  }

  // Round 12: auto-save after 30 s of inactivity OR on tab-hide.
  // The hook reads `enabled` continuously, so it only fires when dirty
  // and a class is selected (no false positives on empty state).
  const { touch: touchAutoSave } = useAutoSave(
    async () => { if (dirty && classId && editable) await save(); },
    300000,
    dirty && !!classId && editable,
  );

  async function save() {
    if (!classId) return;
    setSaving(true);
    setSaveModal({ show: true, status: 'saving', message: 'Saving comments…' });
    try {
      const id = `${year}-${term}-${classId}`;
      const entries: Record<string, StudentReportComments> = {};
      roster.forEach((s) => {
        const prev = existing[s.id] ?? {
          classTeacherComment: '',
          hodComment: '',
        };
        // Strip any `undefined` values lurking on `prev` (from earlier
        // writes that didn't clean themselves) before spreading. The
        // outer payload is also cleaned again on the way out — belt
        // and braces, since Firestore rejects undefined anywhere.
        const cleanedPrev = stripUndefined(
          prev as unknown as Record<string, unknown>,
        ) as unknown as StudentReportComments;
        const meta: Record<string, unknown> = {
          authorId: userData?.uid,
          updatedAt: new Date().toISOString(),
        };
        if (userRole) meta.authorRole = userRole;
        entries[s.id] = {
          ...cleanedPrev,
          [field]: sanitizeComment(drafts[s.id] ?? '', 800),
          [`${field}Meta`]: meta,
        };
      });
      // WIPE GUARD (13.7): never write an empty entries map (merge with {} can
      // replace the field, erasing other reviewers' comments on the class doc).
      if (Object.keys(entries).length === 0) return;
      const payload = stripUndefined({
        year,
        term,
        classId,
        entries,
        updatedAt: new Date().toISOString(),
      } as Record<string, unknown>);
      await setDoc(
        doc(db, COLLECTIONS.reportComments, id),
        payload,
        { merge: true },
      );
      setExisting((e) => ({ ...e, ...entries }));
      setDirty(false);
      setSaveModal({ show: true, status: 'success', message: 'Comments saved.' });

      // Round 10: audit with before/after diffs per changed student.
      // Mirrors what CommentReview.tsx does for editor edits, so the
      // student-history view can show comment evolution over time.
      const editDetails: Array<{
        studentId: string;
        studentName: string;
        field: string;
        before: string;
        after: string;
      }> = [];
      const changedStudents = roster.filter((s) => {
        const prev = existing[s.id];
        const draftRaw = drafts[s.id] ?? '';
        const after = sanitizeComment(draftRaw, 800);
        const before = (prev?.[field as keyof typeof prev] as string | undefined) ?? '';
        if (after !== before) {
          editDetails.push({
            studentId: s.id,
            studentName: s.fullName,
            field,
            before,
            after,
          });
          return true;
        }
        return false;
      });
      if (changedStudents.length > 0) {
        auditFromContext(userData, 'comments_saved', 'academic', {
          targetId: classId,
          targetType: 'comment',
          targetName:
            classes.find((c) => c.id === classId)?.name ?? classId,
          details: {
            year,
            term,
            classId,
            field,
            studentsEdited: changedStudents.length,
            studentNames: changedStudents.map((s) => s.fullName),
            studentIds: changedStudents.map((s) => s.id),
            edits: editDetails,
          },
        });
      }
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  const go = (delta: number) => {
    setIdx((i) =>
      Math.max(0, Math.min(studentIds.length - 1, i + delta)),
    );
  };

  /* ---------------- derived for the right pane ---------------- */
  const fieldLabel =
    field === 'hodComment' ? 'Head of Year comment' : 'Class teacher comment';
  const fieldHelp =
    field === 'hodComment'
      ? 'On behalf of the Principal. Review marks, subject comments, and the class teacher\u2019s remarks on the left before writing.'
      : 'Attendance & conduct. Review the left panel first.';

  /* ---------------- derived for the left pane stat row -------- */
  const subs = currentRow?.subjects ?? [];
  const subTotal = subs.length;
  const subWithMark = subs.filter(
    (x) =>
      x.exam === 'ABS' ||
      typeof x.exam === 'number' ||
      typeof x.termMark === 'number',
  ).length;
  const subWithComment = subs.filter(
    (x) => (x.comment ?? '').trim().length > 0,
  ).length;
  const reportComplete =
    subTotal > 0 && subWithMark === subTotal && subWithComment === subTotal;
  const avgMark = (() => {
    const ms = subs
      .map((x) => (x.exam === 'ABS' ? null : x.exam ?? x.termMark))
      .filter((n): n is number => typeof n === 'number');
    return ms.length
      ? Math.round(ms.reduce((a, b) => a + b, 0) / ms.length)
      : null;
  })();
  const daysAbsentCount = currentId ? daysAbsent[currentId] ?? 0 : 0;

  /* Class-teacher comment for HOY view */
  const classTeacherComment =
    field === 'hodComment' && currentId
      ? (existing[currentId]?.classTeacherComment ?? '').trim()
      : '';
  const classTeacherMeta =
    field === 'hodComment' && currentId
      ? existing[currentId]?.classTeacherCommentMeta
      : undefined;

  return (
    <div className="space-y-4">
      <GradientHero
        icon={ClipboardCheck}
        title={title}
        subtitle={subtitle}
        actions={
          classId ? (
            <>
              {dirty ? (
                <GlassPill tone="amber">
                  <span className="w-2 h-2 bg-amber-300 rounded-full animate-pulse" />
                  Unsaved
                </GlassPill>
              ) : (
                <GlassPill tone="green">Saved</GlassPill>
              )}
              <button
                onClick={save}
                disabled={!dirty || !editable || saving}
                className={`flex items-center gap-2 px-6 py-3 font-semibold rounded-xl transition-all duration-300 shadow-lg ${
                  !dirty || !editable
                    ? 'bg-white/20 text-white/60 cursor-not-allowed'
                    : 'bg-white text-[#1b3a6b] hover:bg-blue-50 hover:scale-105'
                }`}
              >
                <SaveIcon size={18} />
                {saving ? 'Saving…' : 'Save all'}
              </button>
            </>
          ) : null
        }
      />

      {!editable && (
        <div className="flex items-center gap-2 text-sm rounded-xl px-4 py-3 border bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-300">
          <Lock size={15} /> {termLabel(term)} {year} is {termStateLabel}. Editing is restricted.
        </div>
      )}
      {msg && (
        <div
          className={`text-sm rounded-xl px-4 py-3 border flex items-center gap-2 ${
            msg.ok
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
              : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
          }`}
        >
          {msg.ok ? <Check size={15} /> : null}
          {msg.text}
        </div>
      )}

      <SoftCard pad="p-5">
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Class
        </label>
        <select
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
          className={glassFieldClass('mt-1.5 sm:w-80')}
        >
          <option value="">— select class —</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        {classes.length === 0 && (
          <p className="text-xs text-gray-500 mt-2">
            No classes are assigned to you for this review.
          </p>
        )}
      </SoftCard>

      {classId && loading && <Spinner label="Loading class…" />}

      {classId && !loading && currentStudent && (
        <>
          <div className="grid lg:grid-cols-2 gap-4">
            {/* LEFT — context */}
            <SoftCard pad="p-0" hover={false} className="overflow-hidden">
              {/* Student summary header */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-lg font-bold text-gray-900 dark:text-white truncate">
                      {currentStudent.fullName}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {currentRow?.className} · {subTotal} subjects
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-3xl font-bold text-primary leading-none">
                      {avgMark ?? '–'}
                      {avgMark !== null && (
                        <span className="text-base font-semibold text-gray-400">%</span>
                      )}
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1">average</p>
                  </div>
                </div>

                {/* Inline stat row — subtler than the old gradient blocks */}
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                    <CalendarX size={13} className="text-amber-500" />
                    <span>
                      <strong className="text-gray-800 dark:text-white">{daysAbsentCount}</strong>{' '}
                      {daysAbsentCount === 1 ? 'day' : 'days'} absent
                    </span>
                  </span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <span
                    className={`inline-flex items-center gap-1.5 ${
                      reportComplete
                        ? 'text-emerald-700 dark:text-emerald-300'
                        : 'text-orange-700 dark:text-orange-300'
                    }`}
                  >
                    {reportComplete ? (
                      <CheckCircle2 size={13} />
                    ) : (
                      <span className="w-2 h-2 rounded-full bg-orange-400" />
                    )}
                    <span>
                      {subWithMark}/{subTotal} marks · {subWithComment}/{subTotal} comments
                    </span>
                  </span>
                </div>
              </div>

              {/* Subjects list */}
              <div className="px-5 pb-3">
                <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Subject performance & teacher comments
                </p>
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1 -mr-1">
                  {subs.map((sub, i) => {
                    const gMark = sub.exam === 'ABS' ? 'ABS' : sub.exam ?? sub.termMark;
                    const gc = gradeColor(sub.grade);
                    return (
                      <div
                        key={i}
                        className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 hover:bg-white dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-600 hover:shadow-sm transition-all p-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
                            {sub.name}
                            {sub.teacher && (
                              <span className="text-gray-400 font-normal">
                                {' '}· {sub.teacher}
                              </span>
                            )}
                          </span>
                          <span className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-bold tabular-nums">
                              {gMark ?? '–'}
                              {typeof gMark === 'number' && '%'}
                            </span>
                            <span
                              className="text-xs font-bold text-white px-2 py-0.5 rounded-md shadow-sm"
                              style={{ backgroundColor: rgb(gc) }}
                            >
                              {sub.grade || '-'}
                            </span>
                            {canRaiseFlags && (
                              <button
                                type="button"
                                onClick={() => {
                                  const subjLookup = subjects.find((x) => x.name === sub.name);
                                  setFlagFor({
                                    subjectName: sub.name,
                                    subjectId: subjLookup?.id,
                                    teacherName: sub.teacher,
                                    commentPreview: sub.comment,
                                  });
                                }}
                                title="Flag this comment / mark"
                                className="p-1 rounded-lg text-gray-300 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                              >
                                <Flag size={13} />
                              </button>
                            )}
                          </span>
                        </div>
                        {sub.comment && (
                          <p className="mt-1.5 text-xs text-gray-600 dark:text-gray-300 italic leading-relaxed">
                            &ldquo;{sub.comment}&rdquo;
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {subs.length === 0 && (
                    <p className="text-sm text-gray-400 py-6 text-center">
                      No subject marks entered yet for this student.
                    </p>
                  )}
                </div>
              </div>

              {/* Class-teacher comment block (HOY view only) */}
              {field === 'hodComment' && (
                <div className="border-t border-gray-100 dark:border-gray-700/60 p-5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide flex items-center gap-1.5 text-[#1b3a6b] dark:text-blue-300 mb-2">
                    <UserCheck size={12} />
                    Class teacher&rsquo;s comment
                  </p>
                  {classTeacherComment ? (
                    <blockquote className="relative rounded-xl bg-gradient-to-br from-[#1b3a6b]/[0.05] to-[#d4a92c]/[0.04] dark:from-[#2d5295]/15 dark:to-[#d4a92c]/10 border border-[#2d5295]/15 dark:border-[#2d5295]/30 px-4 py-3">
                      <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200 italic">
                        &ldquo;{classTeacherComment}&rdquo;
                      </p>
                      {classTeacherMeta?.updatedAt && (
                        <p className="text-[10px] text-gray-400 mt-2 not-italic">
                          updated {new Date(classTeacherMeta.updatedAt).toLocaleDateString()}
                        </p>
                      )}
                    </blockquote>
                  ) : (
                    <p className="text-xs text-gray-400 italic px-1">
                      Class teacher has not submitted their comment yet.
                    </p>
                  )}
                </div>
              )}
            </SoftCard>

            {/* RIGHT — action */}
            <SoftCard pad="p-5" hover={false} className="flex flex-col">
              <div className="flex items-center gap-2 mb-1">
                <MessageSquare size={16} className="text-primary" />
                <h3 className="font-bold text-gray-800 dark:text-white">
                  {fieldLabel}
                </h3>
              </div>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                {fieldHelp}
              </p>

              {/* Textarea — clean, no gradient panel, single accent: the focus ring */}
              <textarea
                ref={taRef}
                value={drafts[currentId] ?? ''}
                disabled={!canEditThis}
                onChange={(e) => setDraft(e.target.value)}
                rows={7}
                placeholder={
                  canEditThis
                    ? 'Write the comment…'
                    : 'You cannot edit this student.'
                }
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-[#2d5295] focus:ring-4 focus:ring-[#1b3a6b]/10 focus:outline-none transition-all disabled:opacity-50 resize-none"
              />
              <div className="flex items-center justify-between mt-1.5 px-1">
                <span className="text-[11px] text-gray-400">
                  {(drafts[currentId] ?? '').length}/800
                </span>
              </div>

              {/* Suggestion controls */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <AccentButton
                  icon={Sparkles}
                  onClick={aiSuggest}
                  disabled={!canEditThis}
                  title="Suggest a comment from approved templates"
                >
                  AI Generate
                </AccentButton>
                <button
                  type="button"
                  onClick={() => setSuggestionsOpen((v) => !v)}
                  className="px-3 py-2 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {suggestionsOpen ? 'Hide phrases' : 'Quick phrases'}
                </button>
              </div>

              {suggestionsOpen && (
                <div className="mt-3 space-y-3 overflow-y-auto max-h-[260px] pr-1 -mr-1">
                  {templates.map((g) => (
                    <div key={g.label}>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                        {g.label}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {g.phrases.map((p) => (
                          <button
                            key={p}
                            type="button"
                            disabled={!canEditThis}
                            onClick={() => insertTemplate(p)}
                            className="text-left text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 disabled:opacity-40 transition-colors"
                          >
                            {p}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SoftCard>
          </div>

          {/* BOTTOM — student navigator */}
          <SoftCard pad="p-4" hover={false}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <Button
                variant="ghost"
                icon={<ChevronLeft size={16} />}
                disabled={idx === 0}
                onClick={() => go(-1)}
              >
                Previous
              </Button>

              <div className="flex items-center gap-3 flex-1 justify-center min-w-[200px]">
                <select
                  value={currentId}
                  onChange={(e) =>
                    setIdx(studentIds.indexOf(e.target.value))
                  }
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-800 text-sm max-w-[260px]"
                >
                  {roster.map((s, i) => (
                    <option key={s.id} value={s.id}>
                      {i + 1}. {s.fullName}
                      {(drafts[s.id] ?? '').trim() ? ' ✓' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                variant="ghost"
                icon={<ChevronRight size={16} />}
                disabled={idx === studentIds.length - 1}
                onClick={() => go(1)}
              >
                Next
              </Button>
            </div>

            {/* Progress bar — replaces the capped-at-30 dots */}
            <div className="mt-3 space-y-1.5">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  Student <strong>{idx + 1}</strong> of {studentIds.length}
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-emerald-500" />
                  <strong className="text-gray-800 dark:text-white">{completeCount}</strong>
                  /{studentIds.length} complete
                  <span className="text-gray-400">({completePct}%)</span>
                </span>
              </div>
              <div className="relative h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#2d5295] to-[#d4a92c] transition-all duration-300"
                  style={{ width: `${completePct}%` }}
                />
                {/* Current-student marker on the bar */}
                {studentIds.length > 0 && (
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white border-2 border-primary shadow"
                    style={{
                      left: `calc(${(idx / Math.max(1, studentIds.length - 1)) * 100}% - 5px)`,
                    }}
                  />
                )}
              </div>
            </div>
          </SoftCard>
        </>
      )}
      {flagFor && currentStudent && classId && (
        <RaiseFlagModal
          open
          onClose={() => setFlagFor(null)}
          year={year}
          term={term}
          classId={classId}
          studentId={currentStudent.id}
          studentName={currentStudent.fullName}
          target="subjectComment"
          subjectId={flagFor.subjectId}
          subjectName={flagFor.subjectName}
          teacherName={flagFor.teacherName}
          commentPreview={flagFor.commentPreview}
        />
      )}
      <StickySaveBar
        visible={dirty && editable}
        saving={saving}
        onSave={save}
        message="Unsaved comment edits"
        saveLabel="Save all"
      />
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </div>
  );
}
